import { memo, ReactNode, useCallback, useEffect } from "react";
import { useEnsureSingleVisibleModal } from "./modal.hooks";

export type ModalDetailsTypes =
  | "details"
  | "wide details"
  | "wide details responsive"
  | "medium details";

type ModalType = "center" | "right" | ModalDetailsTypes;

type ModalProps = {
  children: ReactNode;
  header?: ReactNode;
  open?: boolean;
  setOpen: (open: boolean) => void;
  actions?: ReactNode;
  type?: ModalType;
  nullIfClosed?: boolean;
};

const preventMouseScroll = (event: WheelEvent) => {
  event.preventDefault();
  event.stopPropagation();
  return false;
};
const preventKeyBoardScroll = (event: KeyboardEvent) => {
  if ([32, 33, 34, 35, 37, 38, 39, 40].includes(event.keyCode)) {
    event.preventDefault();
    return false;
  }
  return true;
};
const disableScroll = () => {
  document.addEventListener("wheel", preventMouseScroll, { passive: false });
  document.addEventListener("keydown", preventKeyBoardScroll);
};
const enableScroll = () => {
  document.removeEventListener("wheel", preventMouseScroll);
  document.removeEventListener("keydown", preventKeyBoardScroll);
};

const Modal = ({
  open = false,
  setOpen,
  children,
  type = "center",
  header,
  actions,
  nullIfClosed,
}: ModalProps) => {
  useEffect(() => {
    // disable scrolling while a modal is open
    if (open) {
      disableScroll();
    } else {
      enableScroll();
    }
    return enableScroll;
  }, [open]);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  useEnsureSingleVisibleModal({ open, closeModal });

  if (!open && nullIfClosed) {
    return null;
  }

  return (
    <div className={`nds-modal ${type}`} data-open={open.toString()}>
      <div
        role="button"
        tabIndex={-1}
        className="nds-modal-overlay"
        onKeyUp={({ key }) => {
          if (key === "Enter") {
            closeModal();
          }
        }}
        onClick={closeModal}
      ></div>
      <div className="nds-modal-container" data-open={open.toString()}>
        <div
          role="button"
          tabIndex={0}
          className="nds-modal-dismiss"
          onKeyUp={({ key }) => {
            if (key === "Enter") {
              closeModal();
            }
          }}
          onClick={closeModal}
        >
          <span
            className="narmi-icon-x"
            style={{ fontSize: "20px", color: "var(--color-black)" }}
          />
        </div>
        {header ? (
          <>
            <h4 className="nds-sans">{header}</h4>
            <hr className="nds-hr" />
          </>
        ) : (
          ""
        )}
        {children}
        {actions && <div className="nds-modal-action-row">{actions}</div>}
      </div>
    </div>
  );
};

export default memo(Modal);
